import { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, Phone, Handshake } from "lucide-react";
import { COMPANY } from "@/data/content";
import Logo from "@/components/Logo";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Careers", to: "/careers" },
  { label: "Industries", to: "/#industries" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 lg:h-24 items-center justify-between">
          <Logo variant="light" to="/" />


          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-3.5 py-2 text-sm font-medium transition-colors rounded-md ${
                    isActive && !item.to.includes("#")
                      ? "text-primary"
                      : "text-slate-700 hover:text-primary"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/login"
              data-testid="nav-partner-login"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-primary border border-primary/25 bg-primary/5 hover:bg-primary hover:text-white transition-colors"
            >
              <Handshake className="w-4 h-4" strokeWidth={2} />
              Recruitment Partner Login
            </Link>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="nav-call-link"
              className="hidden xl:flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-primary transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={1.75} />
              {COMPANY.phone}
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta-proposal"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold shadow-sm hover:bg-primary-hover transition-colors"
            >
              Request Proposal
            </Link>
          </div>

          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-slate-700 hover:bg-slate-100"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div data-testid="nav-mobile-menu" className="lg:hidden pb-4 pt-2 space-y-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                className="block px-3 py-2.5 rounded-md text-base font-medium text-slate-800 hover:bg-slate-100"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/login"
              data-testid="nav-mobile-partner-login"
              className="mt-2 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary/5 text-primary font-semibold border border-primary/25"
            >
              <Handshake className="w-4 h-4" strokeWidth={2} />
              Recruitment Partner Login
            </Link>
            <Link
              to="/contact"
              data-testid="nav-mobile-cta-proposal"
              className="mt-2 block text-center px-4 py-3 rounded-lg bg-primary text-white font-semibold"
            >
              Request Proposal
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
